/**
 * SharedRide — public /r/:token page. No auth, no header, no login.
 *
 * Family opens the WhatsApp/SMS link and sees a read-only MapLibre map
 * with the driver's whole trail, the live marker (if the trip is still
 * in-progress), and a driver-trust callout (first name + verified badge
 * + rating). Uses the API base URL from REACT_APP_BACKEND_URL directly
 * because we cannot rely on the app's auth-bearing `api` axios instance.
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import axios from "axios";
import { ShieldCheck, Star, MapPin, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedRide() {
  const { token } = useParams();
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const wrapRef = useRef(null);
  const mapRef  = useRef(null);
  const carRef  = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/rideshare/public/playback/${token}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) {
          const st = e?.response?.status;
          if (st === 410) setError("This share link has expired or was revoked.");
          else if (st === 404) setError("Share link not found.");
          else setError("Could not load this ride.");
        }
      }
    };
    load();
    // Re-poll every 15s while the trip is still in-progress.
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  // Initialise the map once we have anchor coords.
  useEffect(() => {
    if (!data || !wrapRef.current || mapRef.current) return;
    const first = data.trail?.[0] || {
      lat: data.live?.lat ?? data.from_lat, lng: data.live?.lng ?? data.from_lng,
    };
    if (first.lat == null) return;
    const map = new maplibregl.Map({
      container: wrapRef.current,
      style: "https://tiles.openfreemap.org/styles/liberty",
      center: [first.lng, first.lat], zoom: 8, attributionControl: false,
    });
    map.on("error", (e) => {
      if (String(e?.error?.message || "").includes("tiles")) {
        try { map.setStyle("https://demotiles.maplibre.org/style.json"); } catch { /* ignore */ }
      }
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    // Anchor markers.
    if (data.from_lat != null) new maplibregl.Marker({ color: "#059669" }).setLngLat([data.from_lng, data.from_lat]).addTo(map);
    if (data.to_lat != null)   new maplibregl.Marker({ color: "#dc2626" }).setLngLat([data.to_lng, data.to_lat]).addTo(map);
    // Trail polyline.
    if (data.trail?.length > 1) {
      const coords = data.trail.map(p => [p.lng, p.lat]);
      const draw = () => {
        if (map.getSource("rs-trail")) return;
        map.addSource("rs-trail", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: coords }}});
        map.addLayer({ id: "rs-trail-line", type: "line", source: "rs-trail",
          paint: { "line-color": "#0369a1", "line-width": 4, "line-opacity": 0.9 },
          layout: { "line-cap": "round", "line-join": "round" }});
        try {
          const b = new maplibregl.LngLatBounds();
          coords.forEach(c => b.extend(c));
          if (data.from_lat != null) b.extend([data.from_lng, data.from_lat]);
          if (data.to_lat != null)   b.extend([data.to_lng,   data.to_lat]);
          map.fitBounds(b, { padding: 40, duration: 400, maxZoom: 12 });
        } catch { /* ignore */ }
      };
      if (map.isStyleLoaded()) draw(); else map.once("load", draw);
    }
    // Live car marker.
    if (data.live?.lat != null) {
      const el = document.createElement("div");
      el.style.cssText = "font-size:26px;text-shadow:0 2px 4px rgba(0,0,0,0.4);";
      el.textContent = "🚗";
      carRef.current = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([data.live.lng, data.live.lat]).addTo(map);
    }
    mapRef.current = map;
    return () => { try { map.remove(); } catch { /* ignore */ } mapRef.current = null; };
  }, [data]);

  // Move live car marker on subsequent polls.
  useEffect(() => {
    if (!mapRef.current || !data?.live?.lat) return;
    if (carRef.current) {
      carRef.current.setLngLat([data.live.lng, data.live.lat]);
    } else {
      const el = document.createElement("div");
      el.style.cssText = "font-size:26px;text-shadow:0 2px 4px rgba(0,0,0,0.4);";
      el.textContent = "🚗";
      carRef.current = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([data.live.lng, data.live.lat]).addTo(mapRef.current);
    }
  }, [data?.live?.lat, data?.live?.lng]);

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center p-4" data-testid="rideshare-share-error">
        <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-sm">
          <div className="text-lg font-bold text-stone-800 mb-1">Ride unavailable</div>
          <div className="text-sm text-stone-500">{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen grid place-items-center text-stone-400 text-sm">Loading ride…</div>;
  }

  const live = data.live;
  return (
    <div className="min-h-screen bg-stone-50" data-testid="rideshare-share-page">
      <div className="bg-emerald-700 text-white px-4 py-3 shadow-md">
        <div className="text-[11px] uppercase tracking-widest font-black opacity-80">Find2Near · Proof of Safety</div>
        <div className="text-lg font-bold mt-0.5 flex items-center gap-2">
          <MapPin size={18}/> {data.from_city} → {data.to_city}
        </div>
      </div>
      <div className="max-w-lg mx-auto p-4 space-y-3">
        <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <div className="text-xs text-stone-500">Driver</div>
            <div className="font-bold text-stone-800 flex items-center gap-2">
              {data.driver.first_name}
              {data.driver.verified && (
                <span className="inline-flex items-center gap-0.5 text-[11px] text-emerald-700 font-bold">
                  <ShieldCheck size={12}/> Verified
                </span>
              )}
            </div>
            <div className="text-[11px] text-stone-500 flex items-center gap-2 mt-0.5">
              {data.driver.avg_rating != null ? (
                <span className="inline-flex items-center gap-0.5">
                  <Star size={11} className="text-amber-500 fill-amber-500"/> {data.driver.avg_rating}
                </span>
              ) : <span>New driver</span>}
              <span>· {data.driver.ride_count || 0} rides completed</span>
            </div>
          </div>
          <StatusPill s={data.status}/>
        </div>

        <div ref={wrapRef} style={{ width: "100%", height: 360 }} className="rounded-2xl overflow-hidden shadow-sm bg-white" data-testid="rideshare-share-map"/>

        {live && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="text-xs text-emerald-700 font-black uppercase tracking-widest flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/> Live · in progress
            </div>
            <div className="mt-2 flex items-center justify-between">
              {live.eta_minutes != null && (
                <div className="text-sm font-bold text-emerald-700" data-testid="rideshare-share-eta">
                  ETA · {Math.round(live.eta_minutes)} min
                </div>
              )}
              {live.speed_kmh != null && (
                <div className="text-xs text-stone-500">{Math.round(live.speed_kmh)} km/h</div>
              )}
            </div>
            {live.progress_pct != null && (
              <div className="mt-2">
                <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${live.progress_pct}%` }}/>
                </div>
                <div className="flex justify-between text-[11px] text-stone-500 mt-1">
                  <span>{Math.round(live.progress_pct)}% travelled</span>
                  {live.remaining_km != null && <span>{live.remaining_km} km left</span>}
                </div>
              </div>
            )}
            {live.updated_at && (
              <div className="text-[11px] text-stone-400 mt-2 flex items-center gap-1">
                <Clock size={11}/> updated {new Date(live.updated_at).toLocaleTimeString()}
              </div>
            )}
          </div>
        )}

        {!live && data.status === "completed" && (
          <div className="bg-white rounded-2xl p-4 shadow-sm text-sm text-stone-700">
            Ride completed at {new Date(data.completed_at).toLocaleString()}.
            The trail below is the driver's full route.
          </div>
        )}
        <div className="text-[11px] text-stone-400 text-center pt-2">
          Share links auto-expire 48 hours after the ride completes. Powered by Find2Near.
        </div>
      </div>
    </div>
  );
}

function StatusPill({ s }) {
  const c = ({
    published: "bg-stone-100 text-stone-700",
    started:   "bg-sky-100 text-sky-700",
    completed: "bg-emerald-600 text-white",
    cancelled: "bg-rose-100 text-rose-700",
  }[s] || "bg-stone-100 text-stone-500");
  return <span className={`text-[10px] uppercase tracking-widest font-black rounded-full px-2 py-1 ${c}`}>{s}</span>;
}
